import { useState } from "react";

type CopiedValue = string | null;

type CopyFn = (text: string) => Promise<boolean>;

type ReturnType = {
  copy: CopyFn;
  copiedText: CopiedValue;
};

/**
 * The way to the clipboard that works where the Clipboard API does not: a
 * selection in an offscreen field and the old copy command. In-app browsers
 * (Telegram's among them) and pages not served over HTTPS have no
 * `navigator.clipboard`, and a tap on "Copy" there used to do nothing at all.
 */
const copyWithSelection = (text: string): boolean => {
  if (typeof document === "undefined") return false;

  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.top = "0";
  field.style.left = "0";
  field.style.opacity = "0";
  document.body.appendChild(field);
  field.select();
  field.setSelectionRange(0, text.length);

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    field.remove();
  }
};

/** Puts `text` on the clipboard; false when neither way worked. */
export const copyText = async (text: string): Promise<boolean> => {
  if (navigator?.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the selection route below.
    }
  }

  return copyWithSelection(text);
};

export function useCopyToClipboard(): ReturnType {
  const [copiedText, setCopiedText] = useState<CopiedValue>(null);

  const copy: CopyFn = async (text) => {
    const copied = await copyText(text);
    setCopiedText(copied ? text : null);

    return copied;
  };

  return { copiedText, copy };
}
